import axios, { AxiosResponse, InternalAxiosRequestConfig } from "axios";

// Tạo axios instance
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "http://localhost:5000/api",
  headers: {
    "Content-Type": "application/json",
  },
});

// Thêm token vào mỗi request
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Xử lý response errors
api.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error) => {
    // Bỏ qua request đăng nhập — 401 ở đây là sai email/mật khẩu,
    // để LoginPage tự hiển thị lỗi, không redirect/reload xoá mất thông báo
    const isLoginRequest = error.config?.url?.includes("/auth/login");
    if (error.response?.status === 401 && !isLoginRequest) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  },
);

// ========== AUTH APIs ==========
export const authAPI = {
  login: (credentials: { email: string; password: string }) => api.post("/auth/login", credentials),
  register: (userData: Record<string, unknown>) => api.post("/auth/register", userData),
  getMe: () => api.get("/auth/me"),
  forgotPassword: (email: string) => api.post("/auth/forgot-password", { email }),
};

// ========== USERS APIs ==========
export const usersAPI = {
  getAll: () => api.get("/users"),
  getById: (id: string) => api.get(`/users/${id}`),
  create: (userData: Record<string, unknown>) => api.post("/users", userData),
  update: (id: string, userData: Record<string, unknown>) => api.put(`/users/${id}`, userData),
  delete: (id: string) => api.delete(`/users/${id}`),
  ban: (id: string, reason: string) => api.put(`/users/${id}/ban`, { reason }),
  unban: (id: string) => api.put(`/users/${id}/unban`, {}),
  approveCmnd: (id: string) => api.put(`/users/${id}/cmnd/approve`, {}),
  rejectCmnd: (id: string, reason?: string) => api.put(`/users/${id}/cmnd/reject`, { reason }),
};

// ========== ROOMS APIs ==========
export const roomsAPI = {
  getAll: () => api.get("/rooms"),
  getById: (id: string) => api.get(`/rooms/${id}`),
  create: (roomData: Record<string, unknown>) => api.post("/rooms", roomData),
  update: (id: string, roomData: Record<string, unknown>) => api.put(`/rooms/${id}`, roomData),
  delete: (id: string) => api.delete(`/rooms/${id}`),
  toggleFeatured: (id: string) => api.put(`/rooms/${id}/featured`),
  toggleHidden: (id: string) => api.put(`/rooms/${id}/hidden`),
};

// ========== BOOKINGS APIs ==========
export const bookingsAPI = {
  getAll: () => api.get("/bookings"),
  getById: (id: string) => api.get(`/bookings/${id}`),
  create: (bookingData: Record<string, unknown>) => api.post("/bookings", bookingData),
  createLock: (data: Record<string, unknown>) => api.post("/bookings/lock", data),
  update: (id: string, bookingData: Record<string, unknown>) => api.put(`/bookings/${id}`, bookingData),
  delete: (id: string) => api.delete(`/bookings/${id}`),
  filter: (params: Record<string, unknown>) => api.get("/bookings/filter", { params }),
  resendEmail: (id: string) => api.post(`/bookings/${id}/resend-email`),
  syncLLD: () => api.post("/bookings/sync-lld"),
  getLLDTimeReview: () => api.get("/bookings/lld-time-review"),
  approveNoteTime: (id: string, body: { action: "approve" | "keep" | "edit"; checkInTime?: string; checkOutTime?: string }) =>
    api.patch(`/bookings/${id}/approve-note-time`, body),
};

// ========== COMMENTS APIs ==========
export const commentsAPI = {
  getAll: () => api.get("/comments"),
  getById: (id: string) => api.get(`/comments/${id}`),
  create: (commentData: Record<string, unknown>) => api.post("/comments", commentData),
  importOne: (data: Record<string, unknown>) => api.post("/comments/import", data),
  updateStatus: (id: string, status: string) => api.put(`/comments/${id}/status`, { status }),
  reply: (id: string, adminReply: string) => api.put(`/comments/${id}/reply`, { adminReply }),
  delete: (id: string) => api.delete(`/comments/${id}`),
};

// ========== VOUCHERS APIs ==========
export const vouchersAPI = {
  getAll: () => api.get("/vouchers"),
  getHolders: () => api.get("/vouchers/holders"),
  getById: (id: string) => api.get(`/vouchers/${id}`),
  create: (voucherData: Record<string, unknown>) => api.post("/vouchers", voucherData),
  update: (id: string, voucherData: Record<string, unknown>) => api.put(`/vouchers/${id}`, voucherData),
  delete: (id: string) => api.delete(`/vouchers/${id}`),
  toggleStatus: (id: string) => api.put(`/vouchers/${id}/toggle`),
};

// ========== SITE SETTINGS APIs ==========
export const siteSettingsAPI = {
  getSettings: () => api.get("/site-settings"),
  updateHeroBanners: (heroBanners: unknown[]) => api.put("/site-settings/hero-banners", { heroBanners }),
  updatePayment: (data: Record<string, unknown>) => api.put("/site-settings/payment", data),
  updateHolidayMarkup: (data: Record<string, unknown>) => api.put("/site-settings/holiday-markup", data),
  updateSections: (data: { showReviews: boolean }) => api.put("/site-settings/sections", data),
  updateTagline: (data: { tagline: string; taglineEn: string }) => api.put("/site-settings/tagline", data),
  updateSEO: (data: { seoTitle: string; seoDescription: string }) => api.put("/site-settings/seo", data),
};

// ========== UPLOAD APIs ==========
export const uploadAPI = {
  cmnd: (file: File, fileName?: string) => {
    const formData = new FormData();
    formData.append("image", file);
    if (fileName) formData.append("fileName", fileName);
    return api.post("/upload/cmnd", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  inspection: (file: File, fileName?: string) => {
    const formData = new FormData();
    formData.append("image", file);
    if (fileName) formData.append("fileName", fileName);
    return api.post("/upload/inspection", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  invoice: (file: File, fileName?: string) => {
    const formData = new FormData();
    formData.append("file", file);
    if (fileName) formData.append("fileName", fileName);
    return api.post("/upload/invoice", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
};

// ========== ANALYTICS APIs ==========
export const analyticsAPI = {
  getOverview: (month?: string) => api.get(`/analytics/overview${month ? `?month=${month}` : ""}`),
  getRoomStats: (month?: string) => api.get(`/analytics/rooms${month ? `?month=${month}` : ""}`),
  getMonthlyRevenue: (months: number = 12) => api.get(`/analytics/revenue?months=${months}`),
  getCleaningShifts: (month?: string) => api.get(`/analytics/cleaning${month ? `?month=${month}` : ""}`),
  getUpcomingBookings: (limit: number = 8) => api.get(`/analytics/upcoming?limit=${limit}`),
  getCustomerStats: () => api.get("/analytics/customers"),
  getRoomViewDetail: (id: string, days: number = 30) => api.get(`/analytics/rooms/${id}/views?days=${days}`),
};

// ========== SPIN APIs ==========
export const spinAPI = {
  getAll:     () => api.get("/spins"),
  getResults: () => api.get("/spins/results"),
  create:     (data: Record<string, unknown>) => api.post("/spins", data),
  update:     (id: string, data: Record<string, unknown>) => api.put(`/spins/${id}`, data),
  delete:     (id: string) => api.delete(`/spins/${id}`),
};

// ========== ACTIVITY LOG APIs ==========
export const activityLogAPI = {
  create: (data: Record<string, unknown>) => api.post('/activity-logs', data),
  getAll: () => api.get('/activity-logs'),
};

// ========== WAREHOUSE APIs ==========
export const warehousesAPI = {
  getAll: () => api.get("/warehouses"),
  create: (data: Record<string, unknown>) => api.post("/warehouses", data),
  update: (id: string, data: Record<string, unknown>) => api.put(`/warehouses/${id}`, data),
  delete: (id: string) => api.delete(`/warehouses/${id}`),
  // Units
  addUnit: (id: string, data: Record<string, unknown>) => api.post(`/warehouses/${id}/units`, data),
  updateUnit: (id: string, unitId: string, data: Record<string, unknown>) => api.put(`/warehouses/${id}/units/${unitId}`, data),
  deleteUnit: (id: string, unitId: string) => api.delete(`/warehouses/${id}/units/${unitId}`),
  // Unit inventory
  addInventoryItem: (id: string, unitId: string, data: Record<string, unknown>) =>
    api.post(`/warehouses/${id}/units/${unitId}/inventory`, data),
  updateInventoryItem: (id: string, unitId: string, itemId: string, data: Record<string, unknown>) =>
    api.put(`/warehouses/${id}/units/${unitId}/inventory/${itemId}`, data),
  deleteInventoryItem: (id: string, unitId: string, itemId: string) =>
    api.delete(`/warehouses/${id}/units/${unitId}/inventory/${itemId}`),
  // Inspection tickets
  getAllTickets: (id: string, month?: string) =>
    api.get(`/warehouses/${id}/tickets${month ? `?month=${month}` : ""}`),
  getTickets: (id: string, roomName: string) =>
    api.get(`/warehouses/${id}/rooms/${encodeURIComponent(roomName)}/tickets`),
  createTicket: (id: string, roomName: string, data: Record<string, unknown>) =>
    api.post(`/warehouses/${id}/rooms/${encodeURIComponent(roomName)}/tickets`, data),
  updateTicket: (id: string, ticketId: string, data: Record<string, unknown>) =>
    api.put(`/warehouses/${id}/tickets/${ticketId}`, data),
  deleteTicket: (id: string, ticketId: string) =>
    api.delete(`/warehouses/${id}/tickets/${ticketId}`),
  // Monthly imports
  getImport: (id: string, month: string) =>
    api.get(`/warehouses/${id}/imports/${month}`),
  addImportItem: (id: string, month: string, data: Record<string, unknown>) =>
    api.post(`/warehouses/${id}/imports/${month}/items`, data),
  bulkAddImportItems: (id: string, month: string, items: unknown[]) =>
    api.post(`/warehouses/${id}/imports/${month}/items/bulk`, { items }),
  bulkUpdateImportPrices: (id: string, month: string, updates: unknown[]) =>
    api.put(`/warehouses/${id}/imports/${month}/items/bulk-price`, { updates }),
  updateImportItem: (id: string, month: string, itemId: string, data: Record<string, unknown>) =>
    api.put(`/warehouses/${id}/imports/${month}/items/${itemId}`, data),
  deleteImportItem: (id: string, month: string, itemId: string) =>
    api.delete(`/warehouses/${id}/imports/${month}/items/${itemId}`),
  // Bulk inventory
  bulkAddInventoryItems: (id: string, unitId: string, items: unknown[]) =>
    api.post(`/warehouses/${id}/units/${unitId}/inventory/bulk`, { items }),
  // Monthly invoices
  addInvoice: (id: string, month: string, data: Record<string, unknown>) =>
    api.post(`/warehouses/${id}/imports/${month}/invoices`, data),
  deleteInvoice: (id: string, month: string, invoiceId: string) =>
    api.delete(`/warehouses/${id}/imports/${month}/invoices/${invoiceId}`),
  // Transfers (chuyển kho)
  getTransfers: (id: string, month?: string) =>
    api.get(`/warehouses/${id}/transfers${month ? `?month=${month}` : ""}`),
  createTransfer: (id: string, data: Record<string, unknown>) =>
    api.post(`/warehouses/${id}/transfers`, data),
  updateTransfer: (id: string, transferId: string, data: Record<string, unknown>) =>
    api.put(`/warehouses/${id}/transfers/${transferId}`, data),
  deleteTransfer: (id: string, transferId: string) =>
    api.delete(`/warehouses/${id}/transfers/${transferId}`),
  // Stocktake (hàng tồn / kiểm kê)
  getStocktake: (id: string, month: string) =>
    api.get(`/warehouses/${id}/stocktake/${month}`),
  addStocktakeItem: (id: string, month: string, data: Record<string, unknown>) =>
    api.post(`/warehouses/${id}/stocktake/${month}/items`, data),
  bulkAddStocktakeItems: (id: string, month: string, items: unknown[]) =>
    api.post(`/warehouses/${id}/stocktake/${month}/items/bulk`, { items }),
  updateStocktakeItem: (id: string, month: string, itemId: string, data: Record<string, unknown>) =>
    api.put(`/warehouses/${id}/stocktake/${month}/items/${itemId}`, data),
  deleteStocktakeItem: (id: string, month: string, itemId: string) =>
    api.delete(`/warehouses/${id}/stocktake/${month}/items/${itemId}`),
  finalizeStocktake: (id: string, month: string, finalized: boolean = true) =>
    api.put(`/warehouses/${id}/stocktake/${month}/finalize`, { finalized }),
};

// ========== NETFLIX MAIL APIs ==========
export const netflixMailAPI = {
  getAll: () => api.get("/netflix-mail"),
  create: (data: Record<string, unknown>) => api.post("/netflix-mail", data),
  update: (id: string, data: Record<string, unknown>) => api.put(`/netflix-mail/${id}`, data),
  delete: (id: string) => api.delete(`/netflix-mail/${id}`),
  fetchCode: (id: string) => api.post(`/netflix-mail/${id}/fetch-code`),
};

export const sepayAPI = {
  getTransactions: (params: { page?: number; limit?: number; status?: string; resolved?: boolean }) =>
    api.get("/webhooks/sepay/transactions", { params }),
  resolve: (id: string, resolved: boolean, note?: string) =>
    api.patch(`/webhooks/sepay/transactions/${id}/resolve`, { resolved, note }),
  confirmBooking: (id: string, note?: string) =>
    api.post(`/webhooks/sepay/transactions/${id}/confirm-booking`, { note }),
};

export default api;
